import { SectionShell } from './SectionShell';
import { SectionHeader } from './SectionHeader';
import { CARD_ENTRANCE, staggerStyle } from '../../lib/animation';

interface Step {
  readonly num: 1 | 2 | 3;
  readonly title: string;
  readonly body: string;
  readonly numColorClass: string;
}

// 2026-07-02 homepage copy refresh (Georgetown / Frank spec PDF):
// step bodies rewritten to Georgetown's preferred wording. Structural
// shape (3 numbered steps, same colors, same section header) unchanged.
const STEPS: ReadonlyArray<Step> = [
  {
    num: 1,
    title: 'Identify Springboard Occupations',
    body:
      'Across the 55 largest US metro areas, we identified 159 middle-skills occupations that offer both strong starting wages and strong wage growth. These springboard occupations span 22 occupational groups – from healthcare support and management to construction and extraction, transportation and material moving, and protective services.',
    numColorClass: 'bg-navy',
  },
  {
    num: 2,
    title: 'Map Credentials to Jobs',
    body:
      'Using the CIP-SOC crosswalk from the US Bureau of Labor Statistics and the National Center for Education Statistics, we match every program of study at local colleges and training providers with the occupations it prepares workers to enter in each of the 55 metro areas evaluated in this analysis. Because each program can lead to multiple occupations – and each occupation draws from multiple programs – we account for the full web of many-to-many relationships among credentials and careers.',
    numColorClass: 'bg-blue',
  },
  {
    num: 3,
    title: 'Calculate Alignment',
    body:
      'For every metro area and occupational group, we compute a credentials-to-jobs ratio: the annual number of credentials produced that align with springboard occupations divided by the projected annual number of job openings available in occupations that align with those credentials. A ratio below 1.0 signals a shortage; a ratio above 1.0 represents a surplus. Across all 55 metro areas, the average springboard credentials-to-jobs ratio is approximately 0.30 – indicating that institutions across these metro areas are producing only about 30 percent of the credentials needed to meet demand in springboard occupations.',
    numColorClass: 'bg-rose',
  },
];

/**
 * "01 · How It Works" — three numbered step cards explaining the methodology.
 * Mirrors `_archive/home.html` lines 1005–1029.
 */
export function HowItWorksSection() {
  return (
    <SectionShell id="how-it-works">
      <SectionHeader
        eyebrow="01 · How It Works"
        headline={
          <>
            Connecting credentials
            <br />
            to workforce demand
          </>
        }
        lead="We compare the current production of middle-skills credentials (certificates and associate's degrees) that lead to springboard occupations with the projected local labor-market demand for workers in those occupations."
      />

      <ol className="grid gap-5 md:grid-cols-3">
        {STEPS.map((step, idx) => (
          <li
            key={step.num}
            className={`relative overflow-hidden rounded-lg border border-border bg-white px-7 py-9 shadow-card ${CARD_ENTRANCE}`}
            style={staggerStyle(idx)}
          >
            {/* CR-023 (UIS review, 2026-07-31): the giant 96px / 0.035-opacity
                navy ghost numeral that used to sit bottom-right was removed —
                UIS: "big numbers in a very light gray color … impossible to
                see. Remove those numbers." This is a DELIBERATE divergence
                from `_archive/home.html` `.step-card::before` (lines 469–474),
                which still contains the numeral; a future pixel-parity pass
                must NOT reinstate it. The small solid-color numbered badge
                below stays. */}
            <div
              className={`relative mb-5 flex h-7 w-7 items-center justify-center rounded-full text-xs font-extrabold text-white ${step.numColorClass}`}
              aria-hidden="true"
            >
              {step.num}
            </div>
            <h3 className="relative mb-3 text-card-title font-bold text-text-1">{step.title}</h3>
            {/* CR-022: body copy raised text-[13px] → text-base (16px). */}
            <p className="relative text-base leading-[1.78] text-text-3">{step.body}</p>
          </li>
        ))}
      </ol>
    </SectionShell>
  );
}
